import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, query, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper,
  IconButton, Alert, CircularProgress, Chip, Divider, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import { useNavigate, useParams } from 'react-router-dom';
import RoleSelect from '../components/RoleSelect';

export default function TenderTeam() {
  const navigate = useNavigate();
  const { tenderId } = useParams();
  const [tender, setTender] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allProfiles, setAllProfiles] = useState({});
  const [departmentId, setDepartmentId] = useState('');
  const [selectionMode, setSelectionMode] = useState('ai');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [previewVariant, setPreviewVariant] = useState(null);
  const [subDialog, setSubDialog] = useState(false);
  const [subRole, setSubRole] = useState('');
  const [subRequirements, setSubRequirements] = useState('');

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const [tenderSnap, membersSnap, usersSnap] = await Promise.all([
      getDoc(doc(db, 'tenders', tenderId)),
      getDocs(query(collection(db, 'tenderMembers'), where('tenderId', '==', tenderId))),
      getDocs(collection(db, 'users')),
    ]);

    const tenderData = { id: tenderSnap.id, ...tenderSnap.data() };
    setTender(tenderData);
    setDepartmentId(tenderData.departmentId || '');
    setMembers(membersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setAllUsers(users);

    const profiles = {};
    await Promise.all(users.map(async user => {
      const pSnap = await getDoc(doc(db, 'profiles', user.id));
      if (pSnap.exists()) profiles[user.id] = pSnap.data();
    }));
    setAllProfiles(profiles);
    setLoading(false);
  }

  async function handleAISuggestTeam() {
    setAnalyzing(true);
    setError('');
    try {
      const profileSummaries = allUsers
        .filter(u => allProfiles[u.id])
        .map(u => {
          const p = allProfiles[u.id];
          return {
            userId: u.id,
            name: u.name || u.email,
            title: p.personal?.title || '',
            competencies: p.competencies || [],
            tools: [...(p.tools?.expert || []), ...(p.tools?.good || [])],
            standards: p.standards || [],
            industries: p.industries || [],
            roles: p.assignments?.map(a => a.role).filter(Boolean) || [],
            recentProjects: p.assignments?.slice(0, 3).map(a => a.name) || []
          };
        });

      const data = await callClaude({
        model: 'claude-opus-5',
        max_tokens: 2000,
        messages: [{
          role: 'user',
          content: `Du är en expert på att matcha konsulter mot anbud.

Anbudsinformation:
- Namn: ${tender.name}
- Kund: ${tender.client}
- Beskrivning: ${tender.briefingText}
- Efterfrågade roller: ${tender.requiredRoles?.join(', ')}
- Efterfrågade kompetenser: ${tender.requiredSkills?.join(', ')}
- Nyckelord: ${tender.keywords?.join(', ')}

Tillgängliga konsulter:
${JSON.stringify(profileSummaries, null, 2)}

Returnera ENDAST ett JSON-objekt med rankade konsultförslag och eventuella underkonsultbehov:
{
  "suggestions": [
    {
      "userId": "id",
      "matchScore": 85,
      "matchReason": "Motivering på svenska",
      "suggestedRole": "Föreslagen roll i anbudet",
      "strengths": ["styrka 1", "styrka 2"],
      "gaps": ["eventuell lucka 1"]
    }
  ],
  "subcontractorNeeds": [
    {
      "role": "Rollbeteckning",
      "reason": "Varför intern konsult saknas",
      "requirements": {
        "competencies": ["kompetens 1", "kompetens 2"],
        "tools": ["verktyg 1"],
        "standards": ["standard 1"],
        "experience": "Beskrivning av erfarenhetskrav"
      }
    }
  ]
}`
        }]
      });
      const raw = data.content[0].text;
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      for (const suggestion of parsed.suggestions) {
        const existing = members.find(m => m.userId === suggestion.userId);
        if (!existing) {
          await addDoc(collection(db, 'tenderMembers'), {
            tenderId,
            userId: suggestion.userId,
            status: 'suggested',
            aiMatchScore: suggestion.matchScore,
            aiMatchReason: suggestion.matchReason,
            suggestedRole: suggestion.suggestedRole,
            strengths: suggestion.strengths || [],
            gaps: suggestion.gaps || [],
            createdAt: new Date()
          });
        }
      }

      for (const need of (parsed.subcontractorNeeds || [])) {
        await addDoc(collection(db, 'tenderMembers'), {
          tenderId,
          type: 'subcontractor',
          userId: null,
          status: 'needed',
          suggestedRole: need.role,
          reason: need.reason || '',
          requirements: need.requirements || {},
          createdAt: new Date()
        });
      }

      setSaved('AI har föreslagit konsulter!');
      setTimeout(() => setSaved(''), 3000);
      loadAll();
    } catch (err) {
      console.error(err);
      setError('Kunde inte analysera. Försök igen.');
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleUpdateMemberStatus(memberId, status) {
    await updateDoc(doc(db, 'tenderMembers', memberId), { status });
    loadAll();
  }

  async function handleRemoveMember(memberId) {
    await deleteDoc(doc(db, 'tenderMembers', memberId));
    loadAll();
  }

  async function handleUpdateMemberRole(memberId, role) {
    await updateDoc(doc(db, 'tenderMembers', memberId), { suggestedRole: role });
    setMembers(prev => prev.map(m =>
      m.id === memberId ? { ...m, suggestedRole: role } : m
    ));
  }

  async function handleAddManual(userId) {
    const existing = members.find(m => m.userId === userId);
    if (existing) return;
    await addDoc(collection(db, 'tenderMembers'), {
      tenderId,
      userId,
      status: 'confirmed',
      aiMatchScore: null,
      aiMatchReason: null,
      suggestedRole: '',
      strengths: [],
      gaps: [],
      createdAt: new Date()
    });
    setSaved('Konsult tillagd!');
    setTimeout(() => setSaved(''), 2000);
    loadAll();
  }

  async function handleAddSubcontractorNeed() {
    if (!subRole.trim()) return;
    await addDoc(collection(db, 'tenderMembers'), {
      tenderId,
      type: 'subcontractor',
      userId: null,
      status: 'needed',
      suggestedRole: subRole.trim(),
      reason: 'Manuellt tillagd',
      requirements: {
        competencies: [],
        tools: [],
        standards: [],
        experience: subRequirements.trim()
      },
      createdAt: new Date()
    });
    setSubRole('');
    setSubRequirements('');
    setSubDialog(false);
    setSaved('Underkonsultbehov tillagt!');
    setTimeout(() => setSaved(''), 2000);
    loadAll();
  }

  async function handleRemoveSubcontractorNeed(memberId) {
    await deleteDoc(doc(db, 'tenderMembers', memberId));
    loadAll();
  }

  async function handleGenerateVariant(member) {
    setGenerating(true);
    setGeneratingFor(member.userId);
    setError('');
    try {
      const profile = allProfiles[member.userId];
      const user = allUsers.find(u => u.id === member.userId);
      if (!profile) throw new Error('Profil saknas');

      const data = await callClaude({
        model: 'claude-opus-5',
        max_tokens: 4000,
        messages: [{
          role: 'user',
          content: `Du är expert på att anpassa konsultprofiler för anbud.

ANBUDSINFORMATION:
- Namn: ${tender.name}
- Kund: ${tender.client}
- Beskrivning: ${tender.briefingText}
- Efterfrågade roller: ${tender.requiredRoles?.join(', ')}
- Efterfrågade kompetenser: ${tender.requiredSkills?.join(', ')}
- Nyckelord: ${tender.keywords?.join(', ')}
- Konsultens roll i anbudet: ${member.suggestedRole}

KONSULTENS PROFIL:
${JSON.stringify(profile, null, 2)}

Skapa en anpassad version av profilen för detta anbud.
Returnera ENDAST ett JSON-objekt:
{
  "adjustedIngress": "Ny ingress anpassad för anbudet (2-4 meningar)",
  "adjustedAssignments": [lista med de mest relevanta uppdragen max 6 i prioritetsordning],
  "highlightedSkills": ["kompetenser att lyfta fram"],
  "highlightedStandards": ["standarder att lyfta fram"],
  "aiSuggestions": ["Konkret förslag på förbättring 1", "Konkret förslag på förbättring 2"],
  "coverNote": "Kort motivering varför denna konsult passar för anbudet"
}`
        }]
      });
      const raw = data.content[0].text;
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      const existingVariantSnap = await getDocs(
        query(collection(db, 'cvVariants'),
          where('tenderId', '==', tenderId),
          where('userId', '==', member.userId))
      );

      const variantData = {
        tenderId,
        userId: member.userId,
        userName: user?.name || user?.email || '',
        baseSnapshot: { ...profile },
        adjustedIngress: parsed.adjustedIngress || '',
        adjustedAssignments: parsed.adjustedAssignments || profile.assignments || [],
        highlightedSkills: parsed.highlightedSkills || [],
        highlightedStandards: parsed.highlightedStandards || [],
        aiSuggestions: parsed.aiSuggestions || [],
        coverNote: parsed.coverNote || '',
        editedFields: {},
        status: 'draft',
        updatedAt: new Date()
      };

      if (existingVariantSnap.empty) {
        await addDoc(collection(db, 'cvVariants'), {
          ...variantData,
          createdAt: new Date()
        });
      } else {
        await updateDoc(
          doc(db, 'cvVariants', existingVariantSnap.docs[0].id),
          variantData
        );
      }

      await updateDoc(doc(db, 'tenderMembers', member.id), { variantGenerated: true });

      setSaved('CV-variant skapad för ' + (user?.name || 'konsulten') + '!');
      setTimeout(() => setSaved(''), 3000);
      loadAll();
    } catch (err) {
      console.error(err);
      setError('Kunde inte skapa CV-variant. Försök igen.');
    } finally {
      setGenerating(false);
      setGeneratingFor(null);
    }
  }

  async function handlePreviewVariant(member) {
    const snap = await getDocs(
      query(collection(db, 'cvVariants'),
        where('tenderId', '==', tenderId),
        where('userId', '==', member.userId))
    );
    if (!snap.empty) {
      setPreviewVariant({ id: snap.docs[0].id, ...snap.docs[0].data() });
    }
  }

  const confirmedMembers = members.filter(m => m.status === 'confirmed' && m.type !== 'subcontractor');
  const suggestedMembers = members.filter(m => m.status === 'suggested' && m.type !== 'subcontractor');
  const subcontractorNeeds = members.filter(m => m.type === 'subcontractor');

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate('/tenders')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">{tender?.name}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{tender?.client}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {members.length > 0 && (
              <Button variant="outlined" startIcon={<PrintIcon />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                onClick={() => navigate('/tenders/' + tenderId + '/cv-print')}>
                Anbuds-CV (kompakt)
              </Button>
            )}
            {selectionMode === 'ai' && (
              <Button variant="contained" startIcon={<AutoFixHighIcon />}
                sx={{ bgcolor: 'white', color: '#1a1a1a' }}
                onClick={handleAISuggestTeam}
                disabled={analyzing || !tender?.briefingText}>
                {analyzing ? 'Analyserar...' : 'AI - Föreslå konsulter'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {(analyzing || generating) && <LinearProgress sx={{ mb: 2 }} />}

        {/* VÄLJ METOD */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Hur vill du välja konsulter?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Paper variant="outlined"
              onClick={() => setSelectionMode('ai')}
              sx={{
                p: 2.5, flex: 1, cursor: 'pointer', textAlign: 'center',
                bgcolor: selectionMode === 'ai' ? '#1a1a1a' : 'background.paper',
                border: selectionMode === 'ai' ? '2px solid #1a1a1a' : '1px solid',
                borderColor: selectionMode === 'ai' ? '#1a1a1a' : 'divider',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#1a1a1a' }
              }}>
              <Typography fontSize={28}>🤖</Typography>
              <Typography fontWeight="bold"
                sx={{ color: selectionMode === 'ai' ? 'white' : 'text.primary' }}>
                AI-val
              </Typography>
              <Typography variant="caption"
                sx={{ color: selectionMode === 'ai' ? '#aaa' : 'text.secondary' }}>
                Ladda upp FU och låt AI matcha
              </Typography>
            </Paper>
            <Paper variant="outlined"
              onClick={() => setSelectionMode('manual')}
              sx={{
                p: 2.5, flex: 1, cursor: 'pointer', textAlign: 'center',
                bgcolor: selectionMode === 'manual' ? '#1a1a1a' : 'background.paper',
                border: selectionMode === 'manual' ? '2px solid #1a1a1a' : '1px solid',
                borderColor: selectionMode === 'manual' ? '#1a1a1a' : 'divider',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#1a1a1a' }
              }}>
              <Typography fontSize={28}>👤</Typography>
              <Typography fontWeight="bold"
                sx={{ color: selectionMode === 'manual' ? 'white' : 'text.primary' }}>
                Manuellt val
              </Typography>
              <Typography variant="caption"
                sx={{ color: selectionMode === 'manual' ? '#aaa' : 'text.secondary' }}>
                Välj konsulter direkt ur listan
              </Typography>
            </Paper>
          </Box>
        </Paper>

        {/* AI-läge – FU-sammanfattning och varning */}
        {selectionMode === 'ai' && tender?.briefingText && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Förfrågningsunderlag – sammanfattning
            </Typography>
            <Typography variant="body2">{tender.briefingText}</Typography>
            {tender.requiredRoles?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold">Roller: </Typography>
                {tender.requiredRoles.map((r, i) => (
                  <Chip key={i} label={r} size="small" sx={{ mr: 0.5 }} variant="outlined" />
                ))}
              </Box>
            )}
          </Paper>
        )}

        {selectionMode === 'ai' && !tender?.briefingText && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Ladda upp ett förfrågningsunderlag på anbudssidan för att AI ska kunna föreslå konsulter.
            Eller byt till manuellt val.
          </Alert>
        )}

        {/* MANUELLT VAL */}
        {selectionMode === 'manual' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Välj konsulter manuellt
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {allUsers
              .filter(u => allProfiles[u.id])
              .filter(u => !members.find(m => m.userId === u.id))
              .map(user => {
                const profile = allProfiles[user.id];
                return (
                  <Box key={user.id} sx={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', p: 1.5,
                    bgcolor: 'background.default', borderRadius: 1, mb: 1,
                    border: '1px solid', borderColor: 'divider'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={profile?.personal?.photo}
                        sx={{ width: 36, height: 36 }}>
                        {user.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {profile?.personal?.title}
                        </Typography>
                      </Box>
                    </Box>
                    <Button size="small" variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddManual(user.id)}>
                      Lägg till
                    </Button>
                  </Box>
                );
              })}
            {allUsers
              .filter(u => allProfiles[u.id])
              .filter(u => !members.find(m => m.userId === u.id))
              .length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Alla konsulter är redan tillagda.
              </Typography>
            )}
          </Paper>
        )}

        {/* BEKRÄFTADE KONSULTER */}
        {confirmedMembers.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Bekräftade konsulter ({confirmedMembers.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {confirmedMembers.map(member => (
              <MemberCard
                key={member.id}
                member={member}
                user={allUsers.find(u => u.id === member.userId)}
                profile={allProfiles[member.userId]}
                departmentId={departmentId}
                onConfirm={() => handleUpdateMemberStatus(member.id, 'confirmed')}
                onDecline={() => handleUpdateMemberStatus(member.id, 'declined')}
                onRemove={() => handleRemoveMember(member.id)}
                onRoleChange={role => handleUpdateMemberRole(member.id, role)}
                onGenerateVariant={() => handleGenerateVariant(member)}
                onPreviewVariant={() => handlePreviewVariant(member)}
                onEditVariant={() => navigate('/tenders/' + tenderId + '/variant/' + member.userId)}
                generating={generating && generatingFor === member.userId}
              />
            ))}
          </Paper>
        )}

        {/* AI-FÖRSLAG */}
        {suggestedMembers.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              AI-förslag ({suggestedMembers.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {suggestedMembers.map(member => (
              <MemberCard
                key={member.id}
                member={member}
                user={allUsers.find(u => u.id === member.userId)}
                profile={allProfiles[member.userId]}
                departmentId={departmentId}
                onConfirm={() => handleUpdateMemberStatus(member.id, 'confirmed')}
                onDecline={() => handleUpdateMemberStatus(member.id, 'declined')}
                onRemove={() => handleRemoveMember(member.id)}
                onRoleChange={role => handleUpdateMemberRole(member.id, role)}
                onGenerateVariant={() => handleGenerateVariant(member)}
                onPreviewVariant={() => handlePreviewVariant(member)}
                onEditVariant={() => navigate('/tenders/' + tenderId + '/variant/' + member.userId)}
                generating={generating && generatingFor === member.userId}
              />
            ))}
          </Paper>
        )}

        {members.length === 0 && selectionMode === 'ai' && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              Inga konsulter valda ännu.
            </Typography>
            <Button variant="contained" startIcon={<AutoFixHighIcon />}
              onClick={handleAISuggestTeam}
              disabled={analyzing || !tender?.briefingText}>
              Låt AI föreslå konsulter
            </Button>
          </Paper>
        )}

        {members.length === 0 && selectionMode === 'manual' && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Lägg till konsulter från listan ovan.
            </Typography>
          </Paper>
        )}

        {/* UNDERKONSULTBEHOV */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #FF6B00', bgcolor: '#fff8f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddDisabledIcon sx={{ color: '#FF6B00' }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#FF6B00' }}>
                Underkonsulter behövs ({subcontractorNeeds.length})
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<AddIcon />}
              sx={{ borderColor: '#FF6B00', color: '#FF6B00', '&:hover': { borderColor: '#e55a00', bgcolor: '#fff3ec' } }}
              onClick={() => setSubDialog(true)}>
              Lägg till manuellt
            </Button>
          </Box>
          <Divider sx={{ mb: 2, borderColor: '#FF6B00', opacity: 0.3 }} />
          {subcontractorNeeds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Inga underkonsultbehov identifierade ännu.
              {selectionMode === 'ai' && ' Kör AI-analys eller lägg till manuellt.'}
              {selectionMode === 'manual' && ' Lägg till manuellt om en roll inte kan täckas internt.'}
            </Typography>
          ) : (
            subcontractorNeeds.map(need => (
              <SubcontractorCard
                key={need.id}
                need={need}
                onRemove={() => handleRemoveSubcontractorNeed(need.id)}
              />
            ))
          )}
        </Paper>
      </Container>

      {/* DIALOG: Lägg till underkonsultbehov manuellt */}
      <Dialog open={subDialog} onClose={() => setSubDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lägg till underkonsultbehov</DialogTitle>
        <DialogContent>
          <TextField
            label="Roll / befattning"
            fullWidth
            value={subRole}
            onChange={e => setSubRole(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            placeholder="t.ex. BIM-strateg"
          />
          <TextField
            label="Krav och erfarenhet (fritext)"
            fullWidth
            multiline
            rows={4}
            value={subRequirements}
            onChange={e => setSubRequirements(e.target.value)}
            placeholder="Beskriv vilka kompetenser, verktyg och erfarenhet som krävs..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleAddSubcontractorNeed}
            disabled={!subRole.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNABBGRANSKNING */}
      <Dialog open={!!previewVariant} onClose={() => setPreviewVariant(null)}
        maxWidth="md" fullWidth>
        <DialogTitle>CV-variant – {previewVariant?.userName}</DialogTitle>
        <DialogContent dividers>
          {previewVariant && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {previewVariant.coverNote && (
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Motivering
                  </Typography>
                  <Typography variant="body2">{previewVariant.coverNote}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Anpassad ingress
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {previewVariant.adjustedIngress}
                </Typography>
              </Box>
              {previewVariant.highlightedSkills?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Lyfta kompetenser
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {previewVariant.highlightedSkills.map((s, i) => (
                      <Chip key={i} label={s} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {previewVariant.adjustedAssignments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Utvalda uppdrag ({previewVariant.adjustedAssignments.length})
                  </Typography>
                  {previewVariant.adjustedAssignments.map((a, i) => (
                    <Box key={i} sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, mb: 1 }}>
                      <Typography fontWeight="bold">{a.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[a.period, a.client, a.role].filter(Boolean).join(' | ')}
                      </Typography>
                      {a.personalDescription && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {a.personalDescription}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {previewVariant.aiSuggestions?.length > 0 && (
                <Box sx={{ bgcolor: '#fff8e1', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold"
                    color="#f57f17" gutterBottom>
                    AI-förslag på förbättringar
                  </Typography>
                  {previewVariant.aiSuggestions.map((s, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {s}</Typography>
                  ))}
                </Box>
              )}
              {Object.keys(previewVariant.editedFields || {}).length > 0 && (
                <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1,
                  border: '1px solid #FF6B00' }}>
                  <Typography variant="subtitle2" fontWeight="bold"
                    sx={{ color: '#FF6B00' }} gutterBottom>
                    {Object.keys(previewVariant.editedFields).length} fält har editerats
                    från AI-förslaget
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewVariant(null)}>Stäng</Button>
          <Button variant="contained"
            onClick={() => {
              setPreviewVariant(null);
              navigate('/tenders/' + tenderId + '/variant/' + previewVariant?.userId);
            }}>
            Redigera variant
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SubcontractorCard({ need, onRemove }) {
  const req = need.requirements || {};
  const allTags = [
    ...(req.competencies || []),
    ...(req.tools || []),
    ...(req.standards || [])
  ];
  return (
    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, mb: 2,
      border: '1px solid #FFB380' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PersonAddDisabledIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
            <Typography fontWeight="bold">{need.suggestedRole}</Typography>
            <Chip label="Extern part krävs" size="small"
              sx={{ bgcolor: '#FF6B00', color: 'white', fontSize: 11 }} />
          </Box>
          {need.reason && need.reason !== 'Manuellt tillagd' && (
            <Typography variant="body2" color="text.secondary"
              sx={{ fontStyle: 'italic', mb: 1 }}>
              {need.reason}
            </Typography>
          )}
          {req.experience && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {req.experience}
            </Typography>
          )}
          {allTags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {allTags.map((t, i) => (
                <Chip key={i} label={t} size="small" variant="outlined"
                  sx={{ borderColor: '#FF6B00', color: '#FF6B00' }} />
              ))}
            </Box>
          )}
        </Box>
        <Button size="small" variant="text" color="error" onClick={onRemove}>
          Ta bort
        </Button>
      </Box>
    </Box>
  );
}

function MemberCard({ member, user, profile, departmentId, onConfirm, onDecline,
  onRemove, onRoleChange, onGenerateVariant, onPreviewVariant, onEditVariant, generating }) {
  return (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 2,
      border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Avatar src={profile?.personal?.photo} sx={{ width: 48, height: 48 }}>
          {user?.name?.charAt(0) || '?'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography fontWeight="bold">{user?.name || user?.email}</Typography>
              <Typography variant="caption" color="text.secondary">
                {profile?.personal?.title}
              </Typography>
            </Box>
            {member.aiMatchScore && (
              <Chip
                label={'Match: ' + member.aiMatchScore + '%'}
                size="small"
                color={member.aiMatchScore >= 80 ? 'success' :
                  member.aiMatchScore >= 60 ? 'warning' : 'default'}
              />
            )}
          </Box>

          <Box sx={{ mt: 1, maxWidth: 300 }}>
            <RoleSelect
              value={member.suggestedRole || ''}
              onChange={onRoleChange}
              departmentId={departmentId}
              size="small"
            />
          </Box>

          {member.aiMatchReason && (
            <Typography variant="body2" color="text.secondary"
              sx={{ mt: 0.5, fontStyle: 'italic' }}>
              {member.aiMatchReason}
            </Typography>
          )}
          {member.strengths?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {member.strengths.map((s, i) => (
                <Chip key={i} label={s} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 150 }}>
          {member.status === 'suggested' && (
            <>
              <Button size="small" variant="contained" color="success"
                startIcon={<CheckIcon />} onClick={onConfirm}>
                Bekräfta
              </Button>
              <Button size="small" variant="outlined" color="error"
                startIcon={<CloseIcon />} onClick={onDecline}>
                Avvisa
              </Button>
            </>
          )}
          {member.status === 'confirmed' && (
            <>
              <Button size="small" variant="contained"
                startIcon={<AutoFixHighIcon />}
                onClick={onGenerateVariant}
                disabled={generating}
                sx={{ bgcolor: '#1a1a1a', color: 'white',
                  '&:hover': { bgcolor: '#333' } }}>
                {generating ? 'Skapar...' :
                  member.variantGenerated ? 'Uppdatera CV' : 'Skapa CV-variant'}
              </Button>
              {member.variantGenerated && (
                <>
                  <Button size="small" variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={onPreviewVariant}>
                    Snabbvy
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={onEditVariant}>
                    Redigera variant
                  </Button>
                </>
              )}
            </>
          )}
          <Button size="small" variant="text" color="error" onClick={onRemove}>
            Ta bort
          </Button>
        </Box>
      </Box>
    </Box>
  );
}